//not using client side rendering currently as we just need to play trailers and not the whole video
"use client";

import {
  EventNamesEnum,
  CTA_SEEK_INTERVAL_SECONDS,
} from "@/constants/constants";
import { useDeviceType } from "@/hooks/useDeviceType";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import { useLazyVideoPlayer } from "@/hooks/useLazyVideoPlayer";
import { useFlutterVideoControl } from "@/hooks/useFlutterVideoControl";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { getUAParser } from "@/utils/lazyUAParser";
import { useComscoreHLSTracking } from "@/hooks/useComscoreHLSTracking";
import type Hls from "hls.js";
import {
  Forward10SecondsIcon,
  Rewind10SecondsIcon,
  PlayIconWithShadow,
  PauseIconWithShadow,
  VolumeHighIcon,
  MuteIcon,
} from "./Icons";

interface VideoPlayerProps {
  id?: string;
  posterUrl?: string;
  videoSrc: string;
  showSlug?: string;
  sendEvent?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  videoEvent?: { eventName: string; properties: { type?: string } };
  className?: string;
  triggerBackOnVideoEnd?: boolean;
  autoPlayOnlyOnMobile?: boolean;
  loop?: boolean;
  disablePictureInPicture?: boolean;
  reducePosterSize?: boolean;
  onPlayGotoLogin?: boolean;
  hasAspectVideo?: boolean;
  showControls?: boolean;
  /** Listen for Flutter pause/resume messages (for webview when app goes to background) */
  listenToFlutterPauseResume?: boolean;
  "data-testid"?: string;
  hideFullscreenControl?: boolean;
  showSkipControls?: boolean;
  comscoreMetadata?: {
    contentType: string;
    language: string;
    c5Code: string;
    contentSlug: string;
  };
}

export const VideoPlayer = ({
  id,
  posterUrl,
  videoSrc,
  sendEvent,
  showSlug,
  autoPlay = true,
  muted = false,
  videoEvent,
  className = "",
  autoPlayOnlyOnMobile = false,
  triggerBackOnVideoEnd = false,
  loop = false,
  disablePictureInPicture = false,
  reducePosterSize = false,
  onPlayGotoLogin = false,
  hasAspectVideo = true,
  showControls = true,
  listenToFlutterPauseResume = false,
  "data-testid": dataTestId = "VideoPlayer",
  hideFullscreenControl = false,
  showSkipControls = false,
  comscoreMetadata,
}: VideoPlayerProps) => {
  const playerRef = useRef<HTMLVideoElement>(null);

  // Listen for Flutter pause/resume messages (webview only)
  useFlutterVideoControl(playerRef, listenToFlutterPauseResume);
  const hlsRef = useRef<Hls | null>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { trackEvent } = useTrackingAnalytics();
  const [hasTracked, setHasTracked] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(muted);
  const { isMobile } = useDeviceType();
  const router = useRouter();

  // Lazy load HLS.js only if needed
  const { HlsClass, isHlsNeeded } = useLazyVideoPlayer({ videoSrc });

  // Comscore Video Metrix tracking
  useComscoreHLSTracking({
    hlsRef,
    contentType: comscoreMetadata?.contentType ?? "",
    language: comscoreMetadata?.language ?? "",
    c5Code: comscoreMetadata?.c5Code ?? "",
    contentSlug: comscoreMetadata?.contentSlug ?? "",
    enabled: !!comscoreMetadata,
    reinitTrigger: videoSrc,
  });

  const handlePlay = useCallback(() => {
    if (onPlayGotoLogin) {
      trackEvent({
        eventName: EventNamesEnum.TRAILER_PLAY_CTA_CLICKED,
        properties: {
          show_slug: showSlug || "",
        },
      });
      router.push(`/login`);
      return;
    }

    if (sendEvent && !hasTracked) {
      trackEvent({
        eventName: videoEvent?.eventName ?? "trailer_started",
        properties: {
          show_slug: showSlug || "",
          type: videoEvent?.properties.type,
        },
        sendTo: [],
      });
      setHasTracked(true); // Prevents further tracking events from being sent for the same video on pause and play
    }
  }, [
    sendEvent,
    hasTracked,
    showSlug,
    trackEvent,
    onPlayGotoLogin,
    router,
    videoEvent,
  ]);

  const handleVideoEnded = () => {
    if (triggerBackOnVideoEnd) {
      router.back();
    }
  };

  const handleSkipForward = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.currentTime = Math.min(
        playerRef.current.currentTime + CTA_SEEK_INTERVAL_SECONDS,
        playerRef.current.duration || 0,
      );
    }
  }, []);

  const handleSkipBackward = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.currentTime = Math.max(
        playerRef.current.currentTime - CTA_SEEK_INTERVAL_SECONDS,
        0,
      );
    }
  }, []);

  const handleTogglePlayPause = useCallback(() => {
    if (playerRef.current) {
      if (playerRef.current.paused) {
        playerRef.current.play();
      } else {
        playerRef.current.pause();
      }
    }
  }, []);

  // Hide controls after 3 seconds (same as native controls)
  const startHideTimer = useCallback(() => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setControlsVisible(false);
    }, 3000);
  }, []);

  const handlePlayingState = useCallback(() => {
    setIsPlaying(true);
    // Start hide timer when playing (same as native controls)
    if (showSkipControls) {
      startHideTimer();
    }
  }, [showSkipControls, startHideTimer]);

  const handlePausedState = useCallback(() => {
    setIsPlaying(false);
    // Show controls when paused (same as native controls)
    if (showSkipControls) {
      setControlsVisible(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    }
  }, [showSkipControls]);

  // Show controls on interaction and reset timer
  const handleVideoInteraction = useCallback(() => {
    if (showSkipControls && isPlaying) {
      setControlsVisible(true);
      startHideTimer();
    }
  }, [showSkipControls, isPlaying, startHideTimer]);

  // Handle time update for progress bar
  const handleTimeUpdate = useCallback(() => {
    if (playerRef.current) {
      setCurrentTime(playerRef.current.currentTime);
    }
  }, []);

  // Handle duration change
  const handleLoadedMetadata = useCallback(() => {
    if (playerRef.current) {
      setDuration(playerRef.current.duration);
    }
  }, []);

  // Handle seek via progress bar
  const handleSeek = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    if (playerRef.current) {
      playerRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  }, []);

  // Handle mute toggle
  const handleToggleMute = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.muted = !playerRef.current.muted;
      setIsMuted(playerRef.current.muted);
    }
  }, []);

  // Format time as MM:SS
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  // iOS: optionally disable native PiP controls
  useEffect(() => {
    if (!disablePictureInPicture) return;

    (async () => {
      const parser = await getUAParser();
      const osName = parser.getOS().name;
      if (osName === "iOS" && playerRef.current) {
        // refer: https://stagedotin.slack.com/archives/C080EJU9873/p1749013084178759?thread_ts=1748855633.135659&cid=C080EJU9873
        playerRef.current.controls = false;
      }
    })();
  }, [disablePictureInPicture]);

  // Setup HLS if requested or inferred from URL
  useEffect(() => {
    const video = playerRef.current;
    if (!video) return;

    // Clean existing hls instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    // For HLS videos, wait for HLS.js to load
    if (isHlsNeeded) {
      // Wait for HLS.js to load
      if (!HlsClass) return;

      if (HlsClass.isSupported()) {
        const hls = new HlsClass({ enableWorker: true });
        hlsRef.current = hls;
        hls.attachMedia(video);
        hls.on(HlsClass.Events.MEDIA_ATTACHED, () => {
          hls.loadSource(videoSrc);
        });
      } else {
        // Rely on native playback (Safari/iOS or fallback)
        video.src = videoSrc;
      }
    } else {
      // Non-HLS sources (MP4, etc.) - use native video
      video.src = videoSrc;
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [videoSrc, HlsClass, isHlsNeeded]);

  // Autoplay only on mobile if requested
  useEffect(() => {
    if (autoPlayOnlyOnMobile && isMobile) {
      // This ensures video autoplays only on mobile devices when autoPlayOnlyOnMobile is true.
      // This is important for responsive layouts where the video may be hidden on desktop, preventing unnecessary background playback on desktop devices.
      // usecase: Trailer video on Content Detail page
      playerRef.current?.play();
    }
  }, [autoPlayOnlyOnMobile, isMobile, autoPlay]);

  // construct a _next/static version from the posterUrl
  // eg. http://localhost:3000/_next/image?url=https%3A%2F%2Fmedia.stage.in%2Fepisode%2Fhorizontal%2Fsmall%2FepisodeImage-1736439024583.jpg&w=256&q=75
  const posterUrlStatic = reducePosterSize
    ? `/_next/image?url=${posterUrl}&w=64&q=50`
    : posterUrl;

  return (
    <div
      className="relative"
      onClick={handleVideoInteraction}
      onTouchStart={handleVideoInteraction}
    >
      {/* <Video autoPlay={true} poster={posterUrl} src={videoSrc} /> */}
      <video
        // className="player flex1 mt-0 aspect-[16/9] overflow-hidden rounded-md"
        id={id}
        ref={playerRef}
        src={undefined}
        poster={posterUrlStatic}
        autoPlay={autoPlayOnlyOnMobile ? false : autoPlay}
        controls={showControls}
        muted={muted}
        width="100%"
        height="100%"
        onPlay={(e) => {
          handlePlay();
          handlePlayingState();
        }}
        onPause={handlePausedState}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        data-testid={dataTestId}
        playsInline
        className={`${className} ${hasAspectVideo ? "aspect-video" : ""} ${hideFullscreenControl ? "hide-fullscreen-control" : ""} ${showSkipControls ? "hide-play-control hide-overflow-menu" : ""}`}
        onEnded={handleVideoEnded}
        controlsList={`nodownload ${showControls ? "nofullscreen noremoteplayback" : ""}`}
        loop={loop}
        disablePictureInPicture={disablePictureInPicture}
      />
      {/* Custom Controls Overlay - syncs with native controls visibility */}
      {showSkipControls && (
        <>
          {/* Center controls - Play/Pause and Skip */}
          <div
            className={`pointer-events-none absolute inset-0 flex items-center justify-center px-4 transition-opacity duration-300 ${controlsVisible ? "opacity-100" : "opacity-0"}`}
          >
            <button
              onClick={handleSkipBackward}
              className={`flex items-center justify-center p-4 ${controlsVisible ? "pointer-events-auto" : "pointer-events-none"}`}
              aria-label={`Skip backward ${CTA_SEEK_INTERVAL_SECONDS} seconds`}
            >
              <Rewind10SecondsIcon />
            </button>
            {/* Center Play/Pause button */}
            <button
              onClick={handleTogglePlayPause}
              className={`flex items-center justify-center p-4 ${controlsVisible ? "pointer-events-auto" : "pointer-events-none"}`}
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? <PauseIconWithShadow /> : <PlayIconWithShadow />}
            </button>
            <button
              onClick={handleSkipForward}
              className={`flex items-center justify-center p-4 ${controlsVisible ? "pointer-events-auto" : "pointer-events-none"}`}
              aria-label={`Skip forward ${CTA_SEEK_INTERVAL_SECONDS} seconds`}
            >
              <Forward10SecondsIcon />
            </button>
          </div>

          {/* Bottom controls - Progress bar and Mute */}
          <div
            className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-3 pb-2 pt-6 transition-opacity duration-300 ${controlsVisible ? "opacity-100" : "opacity-0"}`}
          >
            {/* Progress bar */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-white">
                {formatTime(currentTime)}
              </span>
              <input
                type="range"
                min={0}
                max={duration || 0}
                value={currentTime}
                onChange={handleSeek}
                className={`video-progress-bar h-1 flex-1 cursor-pointer appearance-none rounded-full bg-white/30 ${controlsVisible ? "pointer-events-auto" : "pointer-events-none"}`}
                style={{
                  background: `linear-gradient(to right, #fff ${(currentTime / (duration || 1)) * 100}%, rgba(255,255,255,0.3) ${(currentTime / (duration || 1)) * 100}%)`,
                }}
              />
              <span className="text-xs text-white">{formatTime(duration)}</span>
              {/* Mute/Unmute button */}
              <button
                onClick={handleToggleMute}
                className={`flex items-center justify-center p-1 ${controlsVisible ? "pointer-events-auto" : "pointer-events-none"}`}
                aria-label={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? (
                  <MuteIcon className="h-5 w-5 text-white" />
                ) : (
                  <VolumeHighIcon />
                )}
              </button>
            </div>
          </div>
        </>
      )}
      {/* <source src={src} type="video/mp4" /> */}
    </div>
  );
};
